import { useState, useEffect } from 'react';
import api from '../../services/api';
import { Activity, RefreshCw, Users, TrendingUp, BarChart3, Clock } from 'lucide-react';
import { Button } from '../../components/ui/button';

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const HOUR_LABELS = Array.from({ length: 24 }, (_, i) => i === 0 ? '12a' : i < 12 ? `${i}a` : i === 12 ? '12p' : `${i - 12}p`);
const ACTIONS = [
  { value: '', label: 'All Activity' },
  { value: 'login', label: 'Logins' },
  { value: 'deposit', label: 'Deposits' },
  { value: 'withdraw', label: 'Withdrawals' },
  { value: 'game_load', label: 'Game Loads' },
  { value: 'game_redeem', label: 'Game Redeems' },
];

export default function ActivityAnalyticsPage() {
  const [heatmap, setHeatmap] = useState(null);
  const [summary, setSummary] = useState(null);
  const [timeline, setTimeline] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionFilter, setActionFilter] = useState('');
  const [days, setDays] = useState(7);

  useEffect(() => { fetchAll(); }, [actionFilter, days]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const params = `days=${days}${actionFilter ? `&action=${actionFilter}` : ''}`;
      const [hRes, sRes, tRes] = await Promise.all([
        api.get(`/api/admin/analytics/activity/heatmap?${params}`),
        api.get(`/api/admin/analytics/activity/summary?days=${days}`),
        api.get(`/api/admin/analytics/activity/timeline?days=${days}`),
      ]);
      setHeatmap(hRes.data);
      setSummary(sRes.data);
      setTimeline(tRes.data);
    } catch {} finally { setLoading(false); }
  };

  const getColor = (val, max) => {
    if (!max || val === 0) return 'rgba(255,255,255,0.03)';
    const intensity = Math.min(val / max, 1);
    if (intensity < 0.25) return `rgba(212,175,55,${0.1 + intensity * 0.3})`;
    if (intensity < 0.5) return `rgba(212,175,55,${0.2 + intensity * 0.4})`;
    if (intensity < 0.75) return `rgba(234,179,8,${0.3 + intensity * 0.4})`;
    return `rgba(249,115,22,${0.5 + intensity * 0.5})`;
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="spinner" /></div>;

  return (
    <div className="space-y-6" data-testid="activity-analytics-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold flex items-center gap-2"><Activity className="w-5 h-5 text-[#D4AF37]" /> Player Activity</h2>
          <p className="text-sm text-gray-400 mt-0.5">Heatmap and analytics for player behavior</p>
        </div>
        <button onClick={fetchAll} className="p-2 rounded-lg hover:bg-white/5 text-gray-400"><RefreshCw className="w-5 h-5" /></button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <select value={actionFilter} onChange={(e) => setActionFilter(e.target.value)}
          className="bg-[#131822] border border-white/10 rounded-lg px-3 py-2 text-sm text-white" data-testid="action-filter">
          {ACTIONS.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
        </select>
        <select value={days} onChange={(e) => setDays(Number(e.target.value))}
          className="bg-[#131822] border border-white/10 rounded-lg px-3 py-2 text-sm text-white" data-testid="days-filter">
          <option value={7}>Last 7 Days</option>
          <option value={14}>Last 14 Days</option>
          <option value={30}>Last 30 Days</option>
          <option value={90}>Last 90 Days</option>
        </select>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <SumCard icon={Activity} label="Total Events" value={summary.total_events || 0} color="#D4AF37" />
          <SumCard icon={Users} label="Top Players" value={summary.top_players?.length || 0} color="#3B82F6" />
          <SumCard icon={BarChart3} label="Action Types" value={summary.by_action?.length || 0} color="#22c55e" />
          <SumCard icon={Clock} label="Period" value={`${days}d`} color="#8b5cf6" />
        </div>
      )}

      {/* Heatmap */}
      {heatmap && (
        <div className="bg-[#131822] rounded-2xl border border-white/10 p-5">
          <h3 className="text-sm font-semibold text-gray-400 mb-4">Activity Heatmap (Hour x Day)</h3>
          <div className="overflow-x-auto">
            <div className="min-w-[700px]">
              {/* Hour labels */}
              <div className="flex ml-12 mb-1">
                {HOUR_LABELS.map((h, i) => (
                  <div key={i} className="flex-1 text-center text-[8px] text-gray-600">{i % 3 === 0 ? h : ''}</div>
                ))}
              </div>
              {/* Grid rows */}
              {DAY_LABELS.map((day, di) => (
                <div key={di} className="flex items-center mb-0.5">
                  <span className="w-12 text-[10px] text-gray-500 text-right pr-2">{day}</span>
                  <div className="flex flex-1 gap-0.5">
                    {(heatmap.grid?.[di] || Array(24).fill(0)).map((val, hi) => (
                      <div
                        key={hi}
                        className="flex-1 rounded-sm cursor-default transition-all hover:ring-1 hover:ring-white/30"
                        style={{ background: getColor(val, heatmap.max_value), height: 20 }}
                        title={`${day} ${HOUR_LABELS[hi]}: ${val} events`}
                        data-testid={`heatmap-cell-${di}-${hi}`}
                      />
                    ))}
                  </div>
                </div>
              ))}
              {/* Legend */}
              <div className="flex items-center justify-end gap-2 mt-3 mr-2">
                <span className="text-[9px] text-gray-500">Less</span>
                {[0.05, 0.2, 0.4, 0.7, 1].map((i, idx) => (
                  <div key={idx} className="w-4 h-4 rounded-sm" style={{ background: getColor(i * (heatmap.max_value || 1), heatmap.max_value || 1) }} />
                ))}
                <span className="text-[9px] text-gray-500">More</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Activity Breakdown + Top Players */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* By Action */}
        {summary?.by_action?.length > 0 && (
          <div className="bg-[#131822] rounded-2xl border border-white/10 p-5">
            <h3 className="text-sm font-semibold text-gray-400 mb-3 flex items-center gap-2"><BarChart3 className="w-4 h-4 text-[#D4AF37]" /> Activity Breakdown</h3>
            <div className="space-y-2">
              {summary.by_action.map(a => {
                const pct = summary.total_events > 0 ? (a.count / summary.total_events * 100) : 0;
                return (
                  <div key={a.action} className="flex items-center gap-3">
                    <span className="text-xs text-gray-400 w-24 capitalize">{a.action?.replace('_', ' ')}</span>
                    <div className="flex-1 h-5 bg-white/5 rounded-full overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${Math.max(pct, 2)}%`, background: 'linear-gradient(90deg, #D4AF37, #f59e0b)' }} />
                    </div>
                    <span className="text-xs text-gray-300 w-12 text-right">{a.count}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Top Players */}
        {summary?.top_players?.length > 0 && (
          <div className="bg-[#131822] rounded-2xl border border-white/10 p-5">
            <h3 className="text-sm font-semibold text-gray-400 mb-3 flex items-center gap-2"><Users className="w-4 h-4 text-[#3B82F6]" /> Most Active Players</h3>
            <div className="space-y-2">
              {summary.top_players.map((p, i) => (
                <div key={p.user_id} className="flex items-center justify-between bg-[#0B0E14] rounded-lg px-3 py-2">
                  <div className="flex items-center gap-2.5">
                    <span className="text-[10px] w-5 h-5 rounded-full bg-[#D4AF37]/10 text-[#D4AF37] flex items-center justify-center font-bold">{i + 1}</span>
                    <span className="text-sm text-white">{p.username}</span>
                  </div>
                  <span className="text-xs text-[#D4AF37] font-semibold">{p.count} actions</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Daily Timeline */}
      {timeline?.timeline?.length > 0 && (
        <div className="bg-[#131822] rounded-2xl border border-white/10 p-5">
          <h3 className="text-sm font-semibold text-gray-400 mb-3 flex items-center gap-2"><TrendingUp className="w-4 h-4 text-[#22c55e]" /> Daily Activity</h3>
          <div className="flex items-end gap-1 h-32">
            {(() => {
              const maxTotal = Math.max(...timeline.timeline.map(t => t.total || 0), 1);
              return timeline.timeline.map((t, i) => (
                <div key={i} className="flex-1 flex flex-col items-center gap-0.5">
                  <div className="w-full rounded-t" style={{ height: `${Math.max((t.total / maxTotal) * 100, 3)}%`, background: 'linear-gradient(to top, #D4AF37, #f59e0b)' }} title={`${t.date}: ${t.total} events`} />
                  <span className="text-[7px] text-gray-600 rotate-[-45deg] origin-top-left whitespace-nowrap">{t.date?.slice(5)}</span>
                </div>
              ));
            })()}
          </div>
        </div>
      )}

      {/* Empty state */}
      {!summary?.total_events && (
        <div className="text-center py-12 bg-[#131822] rounded-xl border border-white/10">
          <Activity className="w-12 h-12 mx-auto mb-4 text-gray-600" />
          <p className="text-gray-500">No activity data yet. Activity tracking begins when players login, deposit, or play games.</p>
        </div>
      )}
    </div>
  );
}

function SumCard({ icon: Icon, label, value, color }) {
  return (
    <div className="bg-[#131822] rounded-xl border border-white/10 p-4">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${color}15` }}>
          <Icon className="w-5 h-5" style={{ color }} />
        </div>
        <div>
          <p className="text-[10px] text-gray-500">{label}</p>
          <p className="text-lg font-bold text-white">{value}</p>
        </div>
      </div>
    </div>
  );
}
